<script lang="ts">
  import { KNOBS_BY_DOMAIN } from '../../lib/powerKnobs';
  import PowerKnobRow from '../inputs/PowerKnobRow.svelte';
  import SliderRow from '../inputs/SliderRow.svelte';
  import Toggle from '../inputs/Toggle.svelte';
  import Section from '../inputs/Section.svelte';
  import ScaleShadowNotice from '../inputs/ScaleShadowNotice.svelte';
  import { scaleShadows } from '../../lib/tokenModel';
  import { themeState } from '../../lib/theme.svelte';

  // <option> only reliably accepts a background via inline style (no dark:
  // variant support), so it's derived from the chrome theme directly.
  let optionBg = $derived(themeState.value === 'dark' ? '#16161e' : '#ffffff');

  let { overrides, onSet, onReset }: {
    overrides: Record<string, string>;
    onSet: (name: string, value: string) => void;
    onReset: (name: string) => void;
  } = $props();

  const DURATIONS = [
    { label: "Instant", token: "--sf-duration-instant", base: 100, max: 300  },
    { label: "Fast",    token: "--sf-duration-fast",    base: 150, max: 500  },
    { label: "Normal",  token: "--sf-duration-normal",  base: 250, max: 800  },
    { label: "Slow",    token: "--sf-duration-slow",    base: 400, max: 1200 },
    { label: "Slower",  token: "--sf-duration-slower",  base: 600, max: 2000 },
  ];

  const EASINGS = [
    { label: "Linear",     token: "--sf-ease-linear",    value: "linear",                                                      preview: "M0,40 L40,0" },
    { label: "Ease out",   token: "--sf-ease-out",       value: "cubic-bezier(0.25, 0, 0.15, 1)",                              preview: "M0,40 C8,40 28,0 40,0" },
    { label: "Ease in",    token: "--sf-ease-in",        value: "cubic-bezier(0.5, 0, 0.75, 0.25)",                            preview: "M0,40 C12,40 32,32 40,0" },
    { label: "Ease in-out",token: "--sf-ease-in-out",    value: "cubic-bezier(0.4, 0, 0.2, 1)",                                preview: "M0,40 C8,40 32,0 40,0" },
    { label: "Spring",     token: "--sf-ease-spring",    value: "linear(0, 0.5, 1.1, 0.95, 1.02, 1)",                          preview: "M0,40 C10,20 20,-4 28,-2 S38,2 40,0" },
    { label: "Bounce",     token: "--sf-ease-bounce",    value: "linear(0, 0.35 18%, 1 32%, 0.86 42%, 1.02 56%, 0.98 72%, 1)", preview: "M0,40 L12,16 L20,0 L26,6 L32,-1 L36,1 L40,0" },
    { label: "Elastic",    token: "--sf-ease-elastic",   value: "linear(0, 0.3, 1.2, 0.9, 1.05, 1)",                           preview: "M0,40 C8,40 16,-10 24,-2 S36,2 40,0" },
    { label: "Overshoot",  token: "--sf-ease-overshoot", value: "linear(0, 0.6 30%, 1.08 55%, 0.98 75%, 1)",                   preview: "M0,40 C8,40 20,-10 40,0" },
  ];

  // The five fixed per-index stagger delay tokens were removed when
  // .sf-stagger landed; the whole sequence is now one knob, and
  // core/motion.css multiplies it by the child index and --sf-motion-scale.
  const STAGGER_TOKEN = "--sf-stagger-step";
  const STAGGER_DEFAULT_MS = 75;

  const knobs = KNOBS_BY_DOMAIN["motion"] ?? [];

  let scale = $derived((() => { const v = parseFloat(overrides["--sf-motion-scale"] ?? "1"); return isFinite(v) ? v : 1; })());
  let motionDisabled = $derived(overrides["--sf-motion-scale"] === "0");
  let themeTransition = $derived((() => { const v = parseFloat(overrides["--sf-theme-transition-duration"]?.replace("ms","") ?? String(300 * scale)); return isFinite(v) ? v : Math.round(300 * scale); })());
  // The stored knob is the un-scaled step; the preview below shows the
  // effective delay, so --sf-motion-scale is applied for display only.
  let staggerStep = $derived.by(() => {
    const raw = overrides[STAGGER_TOKEN];
    const v = raw ? parseFloat(raw.replace("ms", "")) : NaN;
    return isFinite(v) ? v : STAGGER_DEFAULT_MS;
  });
  let staggerOverridden = $derived(STAGGER_TOKEN in overrides);

  // Animation demo — user picks which easing + duration to feel.
  let animating = $state(false);
  let animOffsetX = $state(0);
  let demoEase = $state("--sf-ease-out");
  let demoDuration = $state("--sf-duration-normal");

  let showDurations = $state(false);

  // Absolute per-duration overrides that shadow the global --sf-motion-scale
  // knob (same detached-scale warning as Type/Spacing, via tokenModel).
  let motionShadow = $derived(scaleShadows(overrides).find((s) => s.family.id === "motion")?.shadowedSteps ?? []);
  let showEasing = $state(false);
  let showStagger = $state(false);
  let showAdvanced = $state(false);

  function getDuration(token: string, base: number): number {
    const raw = overrides[token];
    if (raw) {
      const parsed = parseFloat(raw);
      if (isFinite(parsed)) return parsed;
    }
    return Math.round(base * scale);
  }

  function easeValue(token: string): string {
    return overrides[token] ?? EASINGS.find((e) => e.token === token)?.value ?? "ease";
  }

  function playDemo() {
    if (animating) return;
    const base = DURATIONS.find((d) => d.token === demoDuration)?.base ?? 250;
    const dur = getDuration(demoDuration, base);
    animating = true;
    animOffsetX = 180;
    setTimeout(() => {
      animOffsetX = 0;
      setTimeout(() => { animating = false; }, dur);
    }, dur);
  }

  function setStaggerStep(ms: number) {
    onSet(STAGGER_TOKEN, `${Math.round(ms)}ms`);
  }

  function resetStagger() {
    onReset(STAGGER_TOKEN);
  }
</script>

<div class="p-4 space-y-5">

  <!-- Quick toggle -->
  <div class="flex items-center justify-between p-3 rounded-xl bg-black/4 dark:bg-white/4 border border-black/8 dark:border-white/8">
    <div>
      <div class="text-[11px] font-semibold text-slate-800 dark:text-slate-200">Disable motion</div>
      <div class="text-[9px] text-slate-500 mt-0.5">Respects prefers-reduced-motion</div>
    </div>
    <Toggle
      checked={motionDisabled}
      title={motionDisabled ? "Enable motion" : "Disable motion"}
      onToggle={() => motionDisabled ? onReset("--sf-motion-scale") : onSet("--sf-motion-scale", "0")}
    />
  </div>

  {#if motionDisabled}
    <div class="rounded-lg bg-amber-500/10 border border-amber-500/20 p-2.5">
      <p class="text-[10px] text-amber-700 dark:text-amber-300">Motion is disabled — duration & easing have no effect while scale is 0.</p>
    </div>
  {/if}

  <!-- LIVE DEMO — pick an easing + duration and feel it. Sits at the top so any
       easing/duration edit can be previewed immediately. -->
  <section class="space-y-3">
    <div class="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Live preview</div>
    <div class="bg-black/4 dark:bg-white/4 rounded-xl border border-black/8 dark:border-white/8 p-4 space-y-3">
      <div class="relative h-10 overflow-hidden rounded">
        <div
          class="absolute top-1 h-8 w-8 bg-indigo-500 rounded-lg"
          style={`transform: translateX(${animOffsetX}px); transition: transform ${getDuration(demoDuration, DURATIONS.find(d => d.token === demoDuration)?.base ?? 250)}ms ${easeValue(demoEase)}`}
        ></div>
      </div>
      <div class="flex items-center gap-2">
        <select
          value={demoEase}
          aria-label="Preview easing"
          onchange={(e) => { demoEase = (e.target as HTMLSelectElement).value; }}
          class="flex-1 min-w-0 bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 rounded-lg px-2 py-1.5 text-[10px] text-slate-800 dark:text-slate-200 focus:outline-none focus:border-indigo-500 cursor-pointer"
        >
          {#each EASINGS as e (e.token)}
            <option value={e.token} style={`background:${optionBg};`}>{e.label}</option>
          {/each}
        </select>
        <select
          value={demoDuration}
          aria-label="Preview duration"
          onchange={(e) => { demoDuration = (e.target as HTMLSelectElement).value; }}
          class="flex-1 min-w-0 bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 rounded-lg px-2 py-1.5 text-[10px] text-slate-800 dark:text-slate-200 focus:outline-none focus:border-indigo-500 cursor-pointer"
        >
          {#each DURATIONS as d (d.token)}
            <option value={d.token} style={`background:${optionBg};`}>{d.label}</option>
          {/each}
        </select>
        <button
          onclick={playDemo}
          disabled={animating}
          class="px-3 py-1.5 rounded-lg text-[10px] font-bold border border-indigo-500/40 bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-500/20 transition-all cursor-pointer disabled:opacity-40 shrink-0"
        >{animating ? "…" : "▶ Play"}</button>
      </div>
    </div>
  </section>

  <div class="h-px bg-black/6 dark:bg-white/6"></div>

  <!-- DURATIONS (with inline preview) -->
  <Section title="Durations" bind:open={showDurations}>
      <ScaleShadowNotice tokens={motionShadow} scaleLabel="duration" onClear={() => motionShadow.forEach(onReset)} />
      <p class="text-[10px] text-slate-400 dark:text-slate-600 leading-relaxed">
        Named duration tokens. Drag to set an absolute ms value, overriding the global scale.
        {#if motionDisabled}<span class="text-amber-600 dark:text-amber-400"> (No effect while motion is disabled.)</span>{/if}
      </p>
      {#each DURATIONS as d (d.token)}
        {@const computed = getDuration(d.token, d.base)}
        <SliderRow
          label={d.label}
          value={computed}
          min={0}
          max={d.max}
          step={10}
          unit="ms"
          overridden={d.token in overrides}
          onChange={(v) => onSet(d.token, `${v}ms`)}
          onReset={() => onReset(d.token)}
        />
      {/each}
      <!-- Duration preview, inside the group it refers to -->
      <div class="bg-black/4 dark:bg-white/4 rounded-xl border border-black/8 dark:border-white/8 p-4 space-y-3">
        {#each DURATIONS as d (d.label)}
          {@const actual = getDuration(d.token, d.base)}
          {@const maxDur = Math.max(...DURATIONS.map((x) => getDuration(x.token, x.base)), 1)}
          <div class="flex items-center gap-3">
            <span class="text-[10px] text-slate-500 w-14 shrink-0">{d.label}</span>
            <div class="flex-1 h-1 bg-black/8 dark:bg-white/8 rounded-full overflow-hidden">
              <div class="h-full bg-indigo-500 rounded-full" style={`width: ${(actual / maxDur) * 100}%`}></div>
            </div>
            <span class="text-[9px] font-mono text-slate-500 w-10 text-right">{actual}ms</span>
          </div>
        {/each}
      </div>
  </Section>

  <div class="h-px bg-black/6 dark:bg-white/6"></div>

  <!-- EASING — editable -->
  <Section title="Easing curves" bind:open={showEasing}>
      <p class="text-[10px] text-slate-400 dark:text-slate-600 leading-relaxed">
        Edit the named easing tokens. Accepts any CSS timing function —
        <span class="font-mono text-slate-600 dark:text-slate-400">cubic-bezier(…)</span> or
        <span class="font-mono text-slate-600 dark:text-slate-400">linear(…)</span>. Use “Live preview” above to feel a curve.
      </p>
      <div class="space-y-2">
        {#each EASINGS as e (e.token)}
          {@const isOverridden = e.token in overrides}
          <div class={`p-2.5 rounded-xl border transition-all ${
            isOverridden ? "bg-indigo-500/10 border-indigo-500/30" : "border-black/8 dark:border-white/8 bg-black/3 dark:bg-white/3"
          }`}>
            <div class="flex items-center gap-2.5">
              <svg width="40" height="28" viewBox="0 0 40 28" class="overflow-visible shrink-0">
                <path d={e.preview} stroke="rgb(99 102 241 / 0.6)" stroke-width="1.5" fill="none" stroke-linecap="round" />
              </svg>
              <div class="flex-1 min-w-0">
                <div class="flex items-center justify-between mb-1">
                  <span class="text-[10px] font-semibold text-slate-700 dark:text-slate-300">{e.label}</span>
                  <div class="flex items-center gap-1.5">
                    <button
                      onclick={() => { demoEase = e.token; playDemo(); }}
                      class="text-[8px] text-slate-500 hover:text-indigo-600 dark:hover:text-indigo-400 cursor-pointer"
                    >preview</button>
                    {#if isOverridden}
                      <button onclick={() => onReset(e.token)} class="text-[8px] text-slate-500 hover:text-rose-600 dark:hover:text-rose-400 cursor-pointer">reset</button>
                    {/if}
                  </div>
                </div>
                <input
                  type="text"
                  value={overrides[e.token] ?? ""}
                  placeholder={e.value}
                  oninput={(ev) => {
                    const v = (ev.target as HTMLInputElement).value.trim();
                    v ? onSet(e.token, v) : onReset(e.token);
                  }}
                  class="w-full bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 rounded px-1.5 py-1 text-[9px] font-mono text-slate-700 dark:text-slate-300 placeholder:text-slate-600 focus:outline-none focus:border-indigo-500"
                />
              </div>
            </div>
          </div>
        {/each}
      </div>
  </Section>

  <div class="h-px bg-black/6 dark:bg-white/6"></div>

  <!-- STAGGER -->
  <Section title="Stagger" bind:open={showStagger}>
      <p class="text-[10px] text-slate-400 dark:text-slate-600 leading-relaxed">
        Put <code class="text-slate-600 dark:text-slate-400">.sf-stagger</code> on a parent and each child's
        entrance is delayed by its index × this step × the motion scale. One knob retunes the whole sequence.
      </p>
      <div class="group">
        <div class="flex items-center justify-between mb-1.5">
          <span class="text-[11px] font-semibold text-slate-800 dark:text-slate-200">Stagger step</span>
          {#if staggerOverridden}
            <button onclick={resetStagger} class="text-[9px] text-slate-500 hover:text-rose-600 dark:hover:text-rose-400 cursor-pointer ">reset</button>
          {/if}
        </div>
        <SliderRow
          label="" value={Math.round(staggerStep)} min={0} max={200} step={5} unit="ms"
          help="--sf-stagger-step — per-child delay unit for .sf-stagger"
          overridden={staggerOverridden}
          onChange={(v) => setStaggerStep(v)}
          onReset={resetStagger}
        />
      </div>
      <div class="bg-black/4 dark:bg-white/4 rounded-xl border border-black/8 dark:border-white/8 p-3 space-y-1">
        {#each [1,2,3,4,5] as n (n)}
          {@const delayMs = Math.round(staggerStep * scale * n)}
          {@const maxStaggerDelay = Math.round(staggerStep * scale * 5)}
          <div class="flex items-center gap-2">
            <span class="text-[9px] font-mono text-slate-400 dark:text-slate-600 w-6">–{n}</span>
            <div class="flex-1 h-1.5 bg-black/8 dark:bg-white/8 rounded-full">
              <div class="h-full bg-indigo-500 rounded-full" style={`width: ${maxStaggerDelay > 0 ? (delayMs / maxStaggerDelay) * 100 : 0}%`}></div>
            </div>
            <span class="text-[9px] font-mono text-slate-500 w-10 text-right">{delayMs}ms</span>
          </div>
        {/each}
      </div>
  </Section>

  <div class="h-px bg-black/6 dark:bg-white/6"></div>

  <!-- ADVANCED — global scale power knob, theme transition, scroll timeline -->
  <Section title="Advanced" spacing="space-y-4" variant="advanced" bind:open={showAdvanced}>
      <!-- Global motion scale (power knob) -->
      <div class="space-y-4">
        {#each knobs as k (k.name)}
          <PowerKnobRow
            knob={k}
            {overrides}
            onChange={(name, val) => val === null ? onReset(name) : onSet(name, val)}
          />
        {/each}
      </div>

      <!-- Theme transition -->
      <SliderRow
        label="Theme switch speed" value={themeTransition} min={0} max={600} step={10} unit="ms"
        help="--sf-theme-transition-duration — dark/light color-scheme switch animation speed"
        overridden={"--sf-theme-transition-duration" in overrides}
        onChange={(v) => onSet("--sf-theme-transition-duration", `${v}ms`)}
        onReset={() => onReset("--sf-theme-transition-duration")}
      />

      <!-- Scroll timeline range -->
      <div class="space-y-2">
        <p class="text-[10px] text-slate-400 dark:text-slate-600 leading-relaxed">
          Default animation-range for scroll-driven animations using the
          <span class="font-mono text-slate-600 dark:text-slate-400">.sf-entrance--*</span> /
          <span class="font-mono text-slate-600 dark:text-slate-400">.sf-exit--*</span> macros.
        </p>
        {#each [
          { label: "Entry start", token: "--sf-scroll-timeline-range-start",      placeholder: "entry 0%" },
          { label: "Entry end",   token: "--sf-scroll-timeline-range-end",        placeholder: "cover 30%" },
          { label: "Exit start",  token: "--sf-scroll-timeline-range-exit-start", placeholder: "cover 70%" },
          { label: "Exit end",    token: "--sf-scroll-timeline-range-exit-end",   placeholder: "exit 100%" },
        ] as r (r.token)}
          <div class="flex items-center gap-2">
            <div class="text-[10px] font-semibold text-slate-600 dark:text-slate-400 w-24 shrink-0">{r.label}</div>
            <input
              type="text"
              value={overrides[r.token] ?? ""}
              placeholder={r.placeholder}
              oninput={(e) => {
                const v = (e.target as HTMLInputElement).value.trim();
                v ? onSet(r.token, v) : onReset(r.token);
              }}
              class="flex-1 min-w-0 bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 rounded px-1.5 py-1 text-[9px] font-mono text-slate-700 dark:text-slate-300 placeholder:text-slate-600 focus:outline-none focus:border-indigo-500"
            />
            {#if r.token in overrides}
              <button onclick={() => onReset(r.token)} class="text-[8px] text-slate-500 hover:text-rose-600 dark:hover:text-rose-400 cursor-pointer shrink-0">reset</button>
            {/if}
          </div>
        {/each}
      </div>
  </Section>
</div>
